import { useId, useRef, useState, type DragEvent, type ReactNode } from "react";

import { dateigroesse } from "../lib/format";

export type Dateiart = "pdf" | "png" | "jpeg" | "docx" | "doc";

const MIME: Record<Dateiart, string> = { pdf: "application/pdf", png: "image/png", jpeg: "image/jpeg", docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", doc: "application/msword" };
const ENDUNGEN: Record<Dateiart, string[]> = { pdf: [".pdf"], png: [".png"], jpeg: [".jpg", ".jpeg"], docx: [".docx"], doc: [".doc"] };
const NAMEN: Record<Dateiart, string> = { pdf: "PDF", png: "PNG", jpeg: "JPG", docx: "Word (.docx)", doc: "Word (.doc)" };

/** Magic Bytes prüfen (wie das Backend) – fängt umbenannte Dateien sofort ab. */
async function erkenneArt(datei: File): Promise<Dateiart | null> {
  const kopf = new Uint8Array(await datei.slice(0, 8).arrayBuffer());
  if (kopf[0] === 0x25 && kopf[1] === 0x50 && kopf[2] === 0x44 && kopf[3] === 0x46) return "pdf";
  if (kopf[0] === 0x89 && kopf[1] === 0x50 && kopf[2] === 0x4e && kopf[3] === 0x47) return "png";
  if (kopf[0] === 0xff && kopf[1] === 0xd8 && kopf[2] === 0xff) return "jpeg";
  // Word (.docx) ist ein Zip – ob es wirklich Word ist, prüft der Server.
  if (kopf[0] === 0x50 && kopf[1] === 0x4b && kopf[2] === 0x03 && kopf[3] === 0x04) return "docx";
  // Altes Word-Format (.doc, Word 97–2003): OLE2-Container.
  if (kopf[0] === 0xd0 && kopf[1] === 0xcf && kopf[2] === 0x11 && kopf[3] === 0xe0) return "doc";
  return null;
}

export async function pruefeDatei(datei: File, erlaubt: Dateiart[], maxBytes: number): Promise<string | null> {
  const erlaubteNamen = erlaubt.map((a) => NAMEN[a]).join(", ");
  const name = datei.name.toLowerCase();
  if (!erlaubt.some((a) => ENDUNGEN[a].some((e) => name.endsWith(e)))) return `${datei.name}: nur ${erlaubteNamen} erlaubt`;
  if (datei.size === 0) return `${datei.name}: Datei ist leer`;
  if (datei.size > maxBytes) return `${datei.name}: ${dateigroesse(datei.size)} – maximal ${dateigroesse(maxBytes)} erlaubt`;
  const art = await erkenneArt(datei);
  if (!art || !erlaubt.includes(art)) return `${datei.name}: Inhalt ist kein gültiges ${erlaubteNamen}`;
  return null;
}

interface Props {
  titel: string;
  icon: string;
  beschreibung: string;
  erlaubt: Dateiart[];
  maxBytes: number;
  mehrere?: boolean;
  deaktiviert?: boolean;
  deaktiviertHinweis?: string;
  kopfzeile?: ReactNode;
  beiDateien: (dateien: File[]) => void;
  beiFehler: (meldungen: string[]) => void;
}

export function UploadZone({
  titel, icon, beschreibung, erlaubt, maxBytes, mehrere = false, deaktiviert = false,
  deaktiviertHinweis, kopfzeile, beiDateien, beiFehler,
}: Props) {  // prettier-ignore
  const eingabe = useRef<HTMLInputElement>(null);
  const [ziehen, setZiehen] = useState(false);
  const [pruefe, setPruefe] = useState(false);
  const [fehler, setFehler] = useState<string[]>([]);
  const id = useId();

  async function verarbeiten(liste: FileList | null) {
    if (!liste || deaktiviert) return;
    const dateien = Array.from(liste);
    if (!mehrere && dateien.length > 1) {
      setFehler(["Bitte nur eine Datei auswählen"]);
      beiFehler(["Bitte nur eine Datei auswählen"]);
      return;
    }
    setPruefe(true);
    const ergebnisse = await Promise.all(dateien.map((d) => pruefeDatei(d, erlaubt, maxBytes)));
    setPruefe(false);
    const meldungen = ergebnisse.filter((m): m is string => m !== null);
    const gueltig = dateien.filter((_, i) => ergebnisse[i] === null);
    setFehler(meldungen);
    if (meldungen.length) beiFehler(meldungen);
    if (gueltig.length) beiDateien(gueltig);
    if (eingabe.current) eingabe.current.value = "";
  }

  function beiDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setZiehen(false);
    void verarbeiten(e.dataTransfer.files);
  }

  return (
    <div className="flex flex-col">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          <span aria-hidden className="mr-1.5">
            {icon}
          </span>
          {titel}
        </h3>
        {kopfzeile}
      </div>
      <div
        role="button"
        tabIndex={deaktiviert ? -1 : 0}
        aria-disabled={deaktiviert}
        aria-describedby={`${id}-info`}
        onClick={() => !deaktiviert && eingabe.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !deaktiviert) {
            e.preventDefault();
            eingabe.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!deaktiviert) setZiehen(true);
        }}
        onDragLeave={() => setZiehen(false)}
        onDrop={beiDrop}
        className={`flex min-h-[10rem] flex-1 flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 py-6 text-center transition ${
          deaktiviert
            ? "cursor-not-allowed border-neutral-200 bg-neutral-50 text-neutral-400"
            : ziehen
              ? "cursor-copy border-neutral-900 bg-neutral-100"
              : fehler.length
                ? "cursor-pointer border-ampel-rot/60 bg-ampel-rotHell/40 hover:border-ampel-rot"
                : "cursor-pointer border-neutral-300 bg-white hover:border-neutral-500 hover:bg-neutral-50"
        } focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900`}
      >
        <span className="text-3xl" aria-hidden>
          {icon}
        </span>
        <p className="mt-2 text-sm font-semibold">
          {pruefe ? "Prüfe Datei…" : deaktiviert ? deaktiviertHinweis : "Datei hierher ziehen oder klicken"}
        </p>
        <p id={`${id}-info`} className="mt-1 text-xs text-neutral-500">
          {beschreibung}
        </p>
        <input
          ref={eingabe}
          type="file"
          className="sr-only"
          tabIndex={-1}
          multiple={mehrere}
          accept={erlaubt.map((a) => [MIME[a], ...ENDUNGEN[a]].join(",")).join(",")}
          onChange={(e) => void verarbeiten(e.target.files)}
          disabled={deaktiviert}
        />
      </div>
      {fehler.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-ampel-rot" aria-live="polite">
          {fehler.map((m) => (
            <li key={m}>• {m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
